import { useEffect, useMemo, useState } from 'react';
import {
  listFormSubmissions,
  updateFormSubmissionStatus,
  type FormSubmission,
  type FormSubmissionStatus,
} from '../lib/formSubmissions';

type Filter = 'all' | FormSubmissionStatus;
type Answer = { label: string; value: string };

const contactFields = ['First name', 'Last name', 'Email', 'Phone', 'How they heard about ETA', 'Message'];
const questionnaireFields = ['Child name', 'Goals', 'Timeline', 'Session frequency', 'Home support'];

function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let value = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"') {
      if (quoted && line[index + 1] === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === ',' && !quoted) {
      fields.push(value);
      value = '';
    } else {
      value += character;
    }
  }
  fields.push(value);
  return fields;
}

function getAnswers(submission: FormSubmission): Answer[] {
  const labels = submission.form_type === 'contact' ? contactFields : questionnaireFields;
  return labels.map((label, index) => ({
    label,
    value: parseCsvLine(submission.csv_line)[index] || '',
  }));
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unavailable' : date.toLocaleString();
}

const AdminFormSubmissions: React.FC = () => {
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void listFormSubmissions()
      .then((rows) => { if (active) setSubmissions(rows); })
      .catch(() => { if (active) setError('Unable to load form submissions. Please try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const visibleSubmissions = useMemo(
    () => submissions.filter((submission) => filter === 'all' || submission.status === filter),
    [filter, submissions],
  );

  const setStatus = async (submission: FormSubmission, status: FormSubmissionStatus) => {
    if (busyId) return;
    setBusyId(submission.id);
    setError(null);
    try {
      const updated = await updateFormSubmissionStatus(submission.id, status);
      setSubmissions((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch {
      setError('Unable to update this submission. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const counts = {
    all: submissions.length,
    new: submissions.filter((item) => item.status === 'new').length,
    completed: submissions.filter((item) => item.status === 'completed').length,
    archived: submissions.filter((item) => item.status === 'archived').length,
  };
  const filters: { value: Filter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'completed', label: 'Completed' },
    { value: 'archived', label: 'Archived history' },
  ];

  return (
    <section className="mt-6 rounded-lg bg-white p-4 shadow md:p-6" aria-labelledby="admin-submissions-heading">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 id="admin-submissions-heading" className="text-xl font-semibold text-gray-900">Contact & questionnaire submissions</h3>
          <p className="mt-1 text-sm text-gray-600">Review responses, mark them complete, or archive them without deleting history.</p>
        </div>
        <div className="flex flex-wrap gap-2" role="toolbar" aria-label="Filter form submissions">
          {filters.map((item) => (
            <button
              key={item.value}
              type="button"
              aria-pressed={filter === item.value}
              onClick={() => setFilter(item.value)}
              className={`min-h-[42px] rounded-full border px-3 py-2 text-sm font-medium ${
                filter === item.value
                  ? 'border-mainPink bg-mainPink text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {item.label} ({counts[item.value]})
            </button>
          ))}
        </div>
      </div>

      {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
      {loading ? (
        <p role="status" className="mt-5 text-sm text-gray-500">Loading submissions…</p>
      ) : visibleSubmissions.length === 0 ? (
        <p className="mt-5 rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
          No submissions in this view.
        </p>
      ) : (
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {visibleSubmissions.map((submission) => (
            <article key={submission.id} className="min-w-0 rounded-lg border border-gray-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold capitalize text-gray-900">{submission.form_type} submission</p>
                  <p className="mt-1 text-xs text-gray-500">Received {formatDate(submission.created_at)}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                  submission.status === 'new' ? 'bg-blue-50 text-blue-800'
                    : submission.status === 'completed' ? 'bg-green-50 text-green-800'
                      : 'bg-gray-100 text-gray-700'
                }`}>
                  {submission.status}
                </span>
              </div>
              <dl className="mt-4 space-y-3">
                {getAnswers(submission).map((answer) => (
                  <div key={answer.label} className="grid gap-1 border-t border-gray-100 pt-2 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-3">
                    <dt className="text-xs font-medium text-gray-500">{answer.label}</dt>
                    <dd className="min-w-0 whitespace-pre-wrap break-words text-sm text-gray-900">{answer.value || '—'}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                {submission.status !== 'completed' && (
                  <button
                    type="button"
                    disabled={busyId === submission.id}
                    onClick={() => void setStatus(submission, 'completed')}
                    className="min-h-[40px] rounded border border-green-200 px-3 py-2 text-sm font-medium text-green-800 hover:bg-green-50 disabled:opacity-50"
                  >
                    Mark complete
                  </button>
                )}
                {submission.status !== 'archived' ? (
                  <button
                    type="button"
                    disabled={busyId === submission.id}
                    onClick={() => void setStatus(submission, 'archived')}
                    className="min-h-[40px] rounded border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Archive
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busyId === submission.id}
                    onClick={() => void setStatus(submission, 'new')}
                    className="min-h-[40px] rounded border border-mainBlue/20 px-3 py-2 text-sm font-medium text-mainBlue hover:bg-blue-50 disabled:opacity-50"
                  >
                    Restore to new
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default AdminFormSubmissions;
